import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Sku } from 'src/app/common/Sku';
import { ApiServiceBase } from './ApiServiceBase';
import { SkuPostModel } from '../common/SkuPostModel';
import { FilePostDto } from '../common/FilePostDto';
import { PagedResult, PagingParams, pagingToHttpParams } from '../common/PagedResult';

@Injectable({
  providedIn: 'root',
})
export class SkuService extends ApiServiceBase {
  protected readonly path = 'sku';

  create(sku: SkuPostModel): Observable<Sku> {
    return this.http
      .post<Sku>(this.getUrl(), sku);
  }

  getById(id: string): Observable<Sku> {
    return this.http.get<Sku>(this.getUrl() + '/' + id);
  }

  getImageBySkuId(id: string, largeImage: boolean): Observable<Blob> {
    let url = this.getImageUrl(id);
    return this.http
      .get(url, { responseType: 'blob', params: { largeImage: largeImage } });
  }

  get(
    isActive: boolean,
    isInactive: boolean,
    name?: string,
    productCategoryId?: string,
    paging?: PagingParams
  ): Observable<PagedResult<Sku>> {
    return this.http
      .get<PagedResult<Sku>>(this.getUrl(), {
        params: {
          name: name,
          productCategoryId: productCategoryId == null ? '' : productCategoryId,
          isActive: isActive,
          isInactive: isInactive,
          ...pagingToHttpParams(paging),
        },
      });
  }

  createMultipart(skuPostModel: SkuPostModel, fileTransactions: FilePostDto[]): Observable<Sku> {
    const formData = this.buildMultipartFormData(skuPostModel, fileTransactions);
    return this.http.post<Sku>(this.getUrl(), formData);
  }

  updateMultipart(
    id: string,
    skuPostModel: SkuPostModel,
    fileTransactions: FilePostDto[],
  ): Observable<Sku> {
    const formData = this.buildMultipartFormData(skuPostModel, fileTransactions);
    return this.http.put<Sku>(this.getUrl() + '/' + id, formData);
  }

  private buildMultipartFormData(
    skuPostModel: SkuPostModel,
    fileTransactions: FilePostDto[],
  ): FormData {
    const formData: FormData = new FormData();

    // New images travel as multipart parts carrying the picked File's raw
    // binary (the backend binds them as IFormFile and stores the bytes as-is).
    fileTransactions.forEach((f) => {
      if (f.isCreated && f.filePayload?.rawFile) {
        formData.append('Images', f.filePayload.rawFile, f.filePayload.originalFileName);
      }
    });

    skuPostModel.filesToDelete = fileTransactions
      .filter((f) => f.isDeleted && f.filePayload?.id)
      .map((f) => f.filePayload.id);

    // The SkuPostModel object is built by spreading a loaded Sku (see
    // sku-edit's applyFromSku), so at runtime it still carries the `files`
    // array with every existing image's base64Data — even though the class
    // doesn't declare it. Serializing those into the JSON part re-uploads all
    // images on every save and bloats the body past nginx's limit (413).
    // Images travel as `Images` multipart parts; deletions via `filesToDelete`.
    const { files, ...skuJson } = skuPostModel as SkuPostModel & { files?: unknown };
    formData.append('SkuPostModel', JSON.stringify(skuJson));
    return formData;
  }

  delete(id: number) {
    return this.http.delete(this.getUrl() + '/' + id);
  }

  // Backend #579 (slice 2): the seller's decision on the listing's pending VAT suggestion.
  // Accept applies the suggested rate server-side (prices re-derived) and settles the
  // suggestion; dismiss settles it and changes nothing else. Both return the updated listing.
  acceptVatSuggestion(id: string): Observable<Sku> {
    return this.http
      .post<Sku>(this.getUrl() + '/' + id + '/vatsuggestion/accept', null);
  }

  dismissVatSuggestion(id: string): Observable<Sku> {
    return this.http
      .post<Sku>(this.getUrl() + '/' + id + '/vatsuggestion/dismiss', null);
  }

  protected getImageUrl(skuId: string): string {
    return `${this.getUrl()}/${skuId}/image`;
  }
}
